/**
 * Cookie consent management utilities
 * Handles storing and retrieving cookie consent preferences from localStorage
 */

/**
 * Retrieve cookie consent preferences from localStorage
 * @returns {Object|null} Consent object with essential, analytics, marketing, and timestamp, or null if not set
 */
export const getCookieConsent = () => {
  try {
    const consent = localStorage.getItem('cookieConsent');
    return consent ? JSON.parse(consent) : null;
  } catch (error) {
    console.error('Error reading cookie consent:', error);
    return null;
  }
};

/**
 * Check if user has consented to a specific cookie type
 * @param {string} type - Cookie type: 'essential', 'analytics', or 'marketing'
 * @returns {boolean} True if consent exists for the type
 */
export const hasConsentFor = (type) => {
  const consent = getCookieConsent();
  if (!consent) return false;
  
  // Essential cookies are always allowed
  if (type === 'essential') return true;
  
  return consent[type] === true;
};

/**
 * Set a cookie with expiration
 * @param {string} name - Cookie name
 * @param {string} value - Cookie value
 * @param {number} days - Number of days until expiration (default: 365)
 * @returns {boolean} True if cookie was set successfully (requires consent for non-essential cookies)
 */
export const setCookie = (name, value, days = 365) => {
  const consent = getCookieConsent();
  if (!consent) {
    console.warn('Cookie consent not given. Cannot set cookie:', name);
    return false;
  }

  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
  return true;
};

/**
 * Retrieve a cookie value by name
 * @param {string} name - Cookie name
 * @returns {string|null} Cookie value or null if not found
 */
export const getCookie = (name) => {
  const nameEQ = name + "=";
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
};

/**
 * Delete a cookie
 * @param {string} name - Cookie name
 */
export const deleteCookie = (name) => {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
};

